import { deiterateAPI } from '@/js/external-js/variableModules/utils/deiterateAPI.util';
import { db } from '@/lib/firebase';
import { 
  doc, 
  collection, 
  onSnapshot, 
} from 'firebase/firestore';

/**
 * Interface representing a Policy document.
 */
export interface Policy {
  id: string;
  policyName: string;
  dateAdded?: string;
  dateUpdated?: string;
  policy_history?: Record<string, any>;
}

/**
 * Interface representing a Supporting Document.
 */
export interface SupportingDocument {
  id: string;
  fileName: string;
  dateAdded?: string;
  dateUpdated?: string;
  [key: string]: any;
}

/**
 * Interface representing a Key Document.
 */
export interface KeyDocument {
  id: string;
  cardTitle: string;
  dateAdded?: string;
  dateUpdated?: string;
  [key: string]: any;
}

/**
 * Interface representing all Documentation Data.
 */
export interface DocumentationData {
  policies: Policy[];
  supportingDocuments: SupportingDocument[];
  keyDocuments: KeyDocument[];
}

interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
}

/**
 * Fetches all policies via Thalassa API.
 */
export async function fetchPolicies(companyID: string): Promise<Policy[]> {
  try {
    const response = await deiterateAPI.GET<ApiResponse<Policy[]>>(
      '/v1/documentation/policies',
      {
        backend: 'thalassa',
        companyID,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching policies:', error);
    throw new Error('Failed to load policies');
  }
}

/**
 * Fetches all supporting documents via Thalassa API.
 */
export async function fetchSupportingDocuments(companyID: string): Promise<SupportingDocument[]> {
  try {
    const response = await deiterateAPI.GET<ApiResponse<SupportingDocument[]>>(
      '/v1/documentation/supporting-documents',
      {
        backend: 'thalassa',
        companyID,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching supporting documents:', error);
    throw new Error('Failed to load supporting documents');
  }
}

/**
 * Fetches all key documents via Thalassa API.
 */
export async function fetchKeyDocuments(companyID: string): Promise<KeyDocument[]> {
  try {
    const response = await deiterateAPI.GET<ApiResponse<KeyDocument[]>>(
      '/v1/documentation/key-documents',
      {
        backend: 'thalassa',
        companyID,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching key documents:', error);
    throw new Error('Failed to load key documents');
  }
}

/**
 * Fetches all documentation data (policies, supporting documents, and key documents).
 */
export async function fetchAllDocumentation(companyID: string): Promise<DocumentationData> {
  try {
    const response = await deiterateAPI.GET<ApiResponse<DocumentationData>>(
      '/v1/documentation/all',
      {
        backend: 'thalassa',
        companyID,
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching documentation:', error);
    throw new Error('Failed to load documentation');
  }
}

/**
 * Sets up a real-time listener for documentation changes.
 * Note: This still uses Firestore listeners for real-time updates,
 * but fetches data via Thalassa API when changes are detected.
 */
export function onDocumentationChange(
  companyID: string,
  callback: (data: DocumentationData) => void
): () => void {
  const policiesUnsubscribe = onSnapshot(
    collection(db, `customers/${companyID}/product/policyPackages/policies`),
    async () => {
      const data = await fetchAllDocumentation(companyID);
      callback(data);
    }
  );

  const supportingDocsUnsubscribe = onSnapshot(
    doc(db, `customers/${companyID}/product/policyPackages/ISO27001keyDocuments/otherDocs`),
    async () => {
      const data = await fetchAllDocumentation(companyID);
      callback(data);
    }
  );

  const keyDocsUnsubscribe = onSnapshot(
    doc(db, `customers/${companyID}/product/policyPackages/ISO27001keyDocuments/keyDocuments`),
    async () => {
      const data = await fetchAllDocumentation(companyID);
      callback(data);
    }
  );

  return () => {
    policiesUnsubscribe();
    supportingDocsUnsubscribe();
    keyDocsUnsubscribe();
  };
}

export default {
  fetchPolicies,
  fetchSupportingDocuments,
  fetchKeyDocuments,
  fetchAllDocumentation,
  onDocumentationChange
};
